/**
 * Folklore-record loader unifying ISOF (DK), Nexus2 (DK), SagaMap (NO),
 * Sagnagrunnur (IS), SKVR (FI), plus curated Greenlandic/British/Irish
 * mythology JSON files.
 *
 * Most upstreams are baked into static JSON under /data/folklore-* by a
 * build-time scrape. Two hit the network at runtime: the SPARQL "sagas" call,
 * and Sägenkartan, which is fetched per viewport and capped, so it is the one
 * source here whose completeness depends on where the user is looking. See
 * fetchISoFLegends.
 *
 * localStorage key: fornland-sagas-v4 (7-day TTL).
 */
import type { BBox, FolkloreRecord, Country } from '../types/index.ts';
import { FOLKLORE_CATEGORY_CONFIG, FOLKLORE_CATEGORY_LABELS } from '../constants/index.ts';
import { determineCountry } from '../utils/geo.ts';
import { warnFetch } from '../utils/logErr.ts';

/** Loose shape covering all curated/static folklore JSON files we read. */
interface RawFolkloreRecord {
  id: string;
  name?: string;
  nameEn?: string;
  nameIrish?: string;
  placeName?: string;
  lng: number;
  lat: number;
  text?: string | null;
  textEn?: string;
  summaryEn?: string;
  annotation?: string;
  year?: string | number;
  genre?: string;
  category?: string;
  country?: string;
  collector?: string;
  informant?: string;
  publicationRef?: string;
  region?: string;
  keywordsEn?: string;
  mythCycle?: string;
  characters?: unknown;
  festivalConnection?: string;
  festivalDate?: string;
  province?: string;
  wikidataId?: string;
  wikipediaUrl?: string;
  sourceUrl?: string;
}

interface IsoFDocument {
  id: string;
  title?: string;
  text?: string | null;
  year?: string | number;
  archive?: { country?: string };
  taxonomy?: Array<{ category?: string }>;
  places?: Array<{ name?: string; landskap?: string; location?: { lat?: number; lon?: number } }>;
  persons?: Array<{ name?: string; relation?: string }>;
}

interface SagaProps {
  id?: string | number;
  sagaid?: string | number;
  saganame?: string;
  chapter?: string;
  type?: string;
  action_start?: string | number;
  action_end?: string | number;
  name?: string;
  manuscript_link?: string;
}

/**
 * How many Sägenkartan records one viewport fetch is allowed to carry home.
 * A record costs about 2,9 kB on the wire and the API offers no way to ask for
 * fewer fields, so 200 is already 560 kB. Measured 22/8-2026.
 */
const ISOF_BUDGET = 200;

/** The only two values `archive.country` takes. 1.265 norway + 1.566 sweden = 2.831, no overlap. */
const ISOF_COUNTRIES = ["norway", "sweden"] as const;

const ISOF_URL = "https://ull.isof.se/folkeservice/api/es/documents/";

/**
 * One page of Sägenkartan for a single archive country.
 *
 * `size=0` makes this a counting request: 103 bytes, and `metadata.total` still
 * comes back with `relation: "eq"`, so it is the register's exact count rather
 * than an Elasticsearch estimate. That is what makes the allocation below free
 * to compute, instead of paid for with a page that gets thrown away.
 */
async function isofPage(geoBox: string, country: string, size: number) {
  const url = `${ISOF_URL}?type=sagenkartan&geo_box=${geoBox}&country=${country}&size=${size}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`ISOF API: ${res.status}`);
  const data = await res.json();
  interface IsoFEnvelope { _source?: IsoFDocument }
  const rows = ((data.data ?? []) as Array<IsoFEnvelope | IsoFDocument>)
    .map((d) => ('_source' in d && d._source ? d._source : d as IsoFDocument));
  return { rows, total: (data.metadata?.total?.value ?? rows.length) as number };
}

/**
 * Split a record budget between countries, smallest claim first.
 *
 * Everyone who wants less than an equal share takes what they want and leaves
 * the rest behind for the others, so a country with nothing in the viewport
 * costs the map nothing. Exported for the test, which walks the cases rather
 * than trusting the arithmetic to read correctly.
 */
export function allocateIsofBudget(totals: number[], budget = ISOF_BUDGET): number[] {
  const out = totals.map(() => 0);
  const order = totals.map((t, i) => i).sort((a, b) => totals[a] - totals[b]);
  let left = budget;
  order.forEach((i, seen) => {
    const share = Math.floor(left / (order.length - seen));
    out[i] = Math.min(totals[i], share);
    left -= out[i];
  });
  return out;
}

/**
 * Sägenkartan for the current viewport, split across the two archives it holds.
 *
 * Asking for the whole box at once looks right and is not. The API caps the
 * response at `size` and returns index order, which groups by archive: over a
 * Scandinavian viewport all 200 records came back from Norsk folkeminnesamling
 * and not one of the 1.566 Swedish records reached the map, from the register
 * run by the Swedish institute. Nothing failed and no count looked wrong.
 *
 * Counting first is what keeps this from costing anything. A fixed half each
 * would be worse than the old code in a Swedish-only viewport, where it would
 * halve a complete result, and topping up afterwards means paying for a page
 * and then discarding it, which measured 825 kB against the old 571 kB over
 * southern Norway. Two 103-byte counts decide the split before anything large
 * moves, so the wire cost stays where it was.
 */
export async function fetchISoFLegends(bbox: BBox): Promise<FolkloreRecord[]> {
  const geoBox = `${bbox.maxLat},${bbox.minLng},${bbox.minLat},${bbox.maxLng}`;
  const counts = await Promise.all(ISOF_COUNTRIES.map((c) => isofPage(geoBox, c, 0)));
  const sizes = allocateIsofBudget(counts.map((c) => c.total));
  const pages = await Promise.all(
    ISOF_COUNTRIES.map((c, i) => (sizes[i] > 0 ? isofPage(geoBox, c, sizes[i]) : Promise.resolve({ rows: [] }))),
  );

  return pages
    .flatMap((p) => p.rows)
    .filter((d) => d.places?.[0]?.location?.lat != null && d.places?.[0]?.location?.lon != null)
    .map((d) => {
      const place = d.places![0];
      const loc = place.location!;
      const cat = d.taxonomy?.[0]?.category ?? "A";
      const config = FOLKLORE_CATEGORY_CONFIG[cat];
      const collector = d.persons?.find((p) => p.relation === "c" || p.relation === "collector");
      const informant = d.persons?.find((p) => p.relation === "i" || p.relation === "informant");
      return {
        id: `isof-${d.id}`,
        name: d.title || FOLKLORE_CATEGORY_LABELS[cat]?.sv || "Navnl\u00f8s saga",
        lng: loc.lon!,
        lat: loc.lat!,
        country: (d.archive?.country === "norway" ? "NO" : "SE") as Country,
        source: "isof" as const,
        title: d.title || FOLKLORE_CATEGORY_LABELS[cat]?.sv || "Navnl\u00f8s saga",
        text: d.text || null,
        year: d.year ? parseInt(String(d.year)) : undefined,
        category: config?.superCat ?? "folklife",
        folkloreCatKey: cat,
        placeName: place.name || "",
        landscape: place.landskap || undefined,
        collector: collector?.name || undefined,
        informant: informant?.name || undefined,
        sourceUrl: `https://ull.isof.se/folkeservice/api/es/document/${d.id}`,
      };
    });
}

/** Classify Icelandic Sagnagrunnur entries by category + keywordsEn */
function classifyIcelandicFolklore(originalCat: string, keywordsEn?: string): string {
  // "spirits" → ghosts, "magic" → sorcery (intact mapping)
  if (originalCat === "spirits") return "ghosts";
  if (originalCat === "magic") return "sorcery";

  // "beings" + "household" → reclassify by keywords
  const kw = (keywordsEn || "").toLowerCase();
  if (/\bel(f|ves)\b|hidden folk|trow|fairy|brownie|elven settlement/.test(kw)) return "huldrefolk";
  if (/troll|giant|sea monster|serpent|dragon|water horse|lake monster/.test(kw)) return "monsters";
  if (/witchcraft|magic|sorcerer|spell|enchantment|curse|devil/.test(kw)) return "sorcery";
  if (/ghost|haunting|revenant|undead/.test(kw)) return "ghosts";
  if (/outlaw|local champion|ancient times|king|battle|chieftain/.test(kw)) return "legends";
  return "folklife";
}

/**
 * Load Icelandic saga locations from DataARC Saga Map GeoJSON.
 * 8,748 saga place references across 37 Icelandic sagas.
 * One-time fetch, cached in localStorage (7 days).
 */
let sagaCache: FolkloreRecord[] | null = null;
export function getSagaCache(): FolkloreRecord[] | null { return sagaCache; }
export async function loadIcelandicSagas(): Promise<FolkloreRecord[]> {
  if (sagaCache) return sagaCache;
  const cacheKey = "fornland-sagas-v4";
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { data, ts } = JSON.parse(cached);
      if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000 && data.length > 0) {
        sagaCache = data;
        return data;
      }
    } catch { /* ignore */ }
  }

  try {
    const res = await fetch(
      "https://raw.githubusercontent.com/castuofa/dataarc-source/main/datasets/sagas/sagas.geojson",
      { signal: AbortSignal.timeout(30000) },
    );
    if (!res.ok) throw new Error(`DataARC: ${res.status}`);
    const geojson = await res.json();

    // Keep ALL saga place references (not just Iceland) — assign actual country from coordinates.
    // Sagas reference places across the Norse world (Norway, Greenland, Faroe Islands, etc.)
    const seen = new Set<string>();
    const records: FolkloreRecord[] = ((geojson.features ?? []) as Array<{ geometry?: { coordinates?: number[] }; properties?: SagaProps }>)
      .filter((f): f is { geometry: { coordinates: number[] }; properties?: SagaProps } => Array.isArray(f.geometry?.coordinates) && f.geometry!.coordinates!.length > 0)
      .map((f) => {
        const p = f.properties ?? {};
        const [lng, lat] = f.geometry.coordinates;
        const ck = `${p.sagaid}-${lng.toFixed(3)},${lat.toFixed(3)}`;
        if (seen.has(ck)) return null;
        seen.add(ck);
        const country = determineCountry(lat, lng);
        const sagaName = p.saganame || "Íslendingasaga";
        const actionStart = p.action_start ? parseInt(String(p.action_start)) : undefined;
        const sceneType = p.type ? `(${p.type})` : null;
        const textParts = [sagaName, p.chapter, sceneType, actionStart ? `ca. ${p.action_start}–${p.action_end || "?"}` : null].filter(Boolean);
        return {
          id: `saga-${p.id || `${lng.toFixed(5)},${lat.toFixed(5)}`}`,
          name: sagaName,
          lng: +lng.toFixed(6),
          lat: +lat.toFixed(6),
          country,
          source: "sagamap" as const,
          title: sagaName,
          text: textParts.join(" · "),
          year: actionStart,
          category: "legends",
          placeName: p.name || "",
          sagaReference: sagaName,
          sourceUrl: p.manuscript_link || "https://sagamap.hi.is/is/",
        };
      })
      .filter(Boolean) as FolkloreRecord[];

    sagaCache = records;
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ data: records, ts: Date.now() }));
    } catch { /* localStorage full */ }
    return records;
  } catch (e) {
    warnFetch("loadIcelandicSagas failed:", e);
    return [];
  }
}

/**
 * Fetch Icelandic folklore from Sagnagrunnur (Árni Magnússon Institute).
 * 13,522 folklore legends — huldufólk, trolls, ghosts, elves, etc.
 * One-time fetch, cached in localStorage (7 days).
 * API: https://ismus.is/django/api/folklore/
 */
let sagnagrunnurCache: FolkloreRecord[] | null = null;
export function getSagnagrunnurCache(): FolkloreRecord[] | null { return sagnagrunnurCache; }
/**
 * Load Icelandic folklore from pre-built static JSON (fetched by scripts/fetch-sagnagrunnur.mjs).
 * ~9,500 folklore legends with full text — huldufólk, trolls, ghosts, elves, etc.
 */
export async function loadSagnagrunnurFolklore(): Promise<FolkloreRecord[]> {
  if (sagnagrunnurCache) return sagnagrunnurCache;
  try {
    const res = await fetch("/data/icelandic-folklore-pins.json");
    if (!res.ok) return [];
    const data = await res.json();
    sagnagrunnurCache = data.map((d: RawFolkloreRecord) => {
      // Faroe Islands coords: 61.3-62.5°N, -7.5 to -6.2°W
      const isFO = d.lat >= 61.3 && d.lat <= 62.5 && d.lng >= -7.5 && d.lng <= -6.2;
      return {
      id: `sagnagrunnur-${d.id}`,
      name: d.name || "Þjóðsaga",
      lng: d.lng,
      lat: d.lat,
      country: (isFO ? "FO" : "IS") as Country,
      source: "sagnagrunnur" as const,
      title: d.name || "Þjóðsaga",
      text: d.text || null,
      nameEn: d.nameEn || undefined,
      summaryEn: d.summaryEn || undefined,
      year: d.year ? parseInt(String(d.year)) : undefined,
      category: classifyIcelandicFolklore(d.category || "beings", d.keywordsEn),
      placeName: d.placeName || "",
      collector: d.collector || undefined,
      informant: d.informant || undefined,
      sourceUrl: `https://sagnagrunnur.arnastofnun.is/en/legend/${d.id}`,
      genre: d.keywordsEn || undefined,
    };});
    return sagnagrunnurCache!;
  } catch (e) {
    warnFetch("loadSagnagrunnurFolklore failed:", e);
    return [];
  }
}

/** The Nexus2 repo, which is what scripts/process-nexus2.mjs reads and what we credit. */
export const NEXUS2_REPO_URL = "https://github.com/ScandinavianSection-UCLA/Nexus2";

/**
 * Deep link to one story in Tangherlini's Folklore Nexus viewer.
 *
 * The old etkspace.scandinavian.ucla.edu is gone (NXDOMAIN) and the dfl2db that
 * replaced it has answered 404 since spring 2026, so the viewer at
 * scando.ist.berkeley.edu/folklorenexus is the only live upstream page per story.
 * Its Apache fallback was fixed on 27 Aug 2026, verified on 28 Aug: every path
 * under /folklorenexus/ now returns index.html and the router resolves it.
 *
 * The route is keyed straight off `story_id`, which is the number in our own
 * `nexus2-<n>` ids, and all 900 were checked against the ids baked into the
 * viewer's bundle with none missing. Anything that is not `nexus2-<n>` falls
 * back to the repo, because that fallback serves index.html for *any* path:
 * a wrong id renders the browse view with an HTTP 200 rather than an error,
 * so a guessed link would look like it worked.
 */
export function nexus2StoryUrl(id: string): string {
  const m = /^nexus2-(\d+)$/.exec(id);
  return m ? `https://scando.ist.berkeley.edu/folklorenexus/story/${m[1]}` : NEXUS2_REPO_URL;
}

let danishFolkloreCache: FolkloreRecord[] | null = null;
export function getDanishFolkloreCache(): FolkloreRecord[] | null { return danishFolkloreCache; }
export async function loadDanishFolklore(): Promise<FolkloreRecord[]> {
  if (danishFolkloreCache) return danishFolkloreCache;
  try {
    const res = await fetch("/data/danish-folklore-pins.json");
    if (!res.ok) return [];
    const data = await res.json();
    danishFolkloreCache = data.map((d: RawFolkloreRecord) => ({
      id: d.id,
      name: d.name || d.placeName || "Ukendt fort\u00e6lling",
      lng: d.lng,
      lat: d.lat,
      country: "DK" as const,
      source: "nexus2" as const,
      title: d.name || d.placeName || "Ukendt fort\u00e6lling",
      text: d.text || null,
      textEn: d.textEn || undefined,
      annotation: d.annotation || undefined,
      year: d.year || undefined,
      // The category is baked into the pin file by scripts/build-danish-folklore-data.mjs.
      // Classifying here cannot work: the pin payload carries no text and no annotation,
      // so the haystack is empty and every record lands in the residual category anyway.
      // A second classifier used to sit in this slot as a fallback; see the note where it
      // was removed in utils/format.ts for why a fallback was worse than none.
      category: d.category || "folklife",
      placeName: d.placeName || "",
      genre: d.genre || undefined,
      informant: d.informant || undefined,
      collector: d.collector || "Evald Tang Kristensen",
      publicationRef: d.publicationRef || undefined,
      sourceUrl: nexus2StoryUrl(d.id),
    }));
    return danishFolkloreCache!;
  } catch {
    return [];
  }
}

// --- On-demand text loading for folklore detail views ---
/** Pre-built lookup of folklore record id → text/details. Shape varies per source so values are unstructured JSON. */
type FolkloreTextEntry = Record<string, unknown>;
const folkloreTextCaches = new Map<string, Record<string, FolkloreTextEntry>>();
const folkloreTextLoading = new Map<string, Promise<void>>();

/** Load folklore text/details on-demand when a detail page is opened */
export async function loadFolkloreTexts(source: "icelandic" | "danish"): Promise<Record<string, FolkloreTextEntry>> {
  const file = source === "icelandic" ? "icelandic-folklore-texts.json" : "danish-folklore-texts.json";
  if (folkloreTextCaches.has(source)) return folkloreTextCaches.get(source)!;
  if (folkloreTextLoading.has(source)) {
    await folkloreTextLoading.get(source);
    return folkloreTextCaches.get(source) || {};
  }
  const p = (async () => {
    try {
      const res = await fetch(`/data/${file}`);
      if (res.ok) folkloreTextCaches.set(source, await res.json());
      else folkloreTextCaches.set(source, {});
    } catch { folkloreTextCaches.set(source, {}); }
  })();
  folkloreTextLoading.set(source, p);
  await p;
  return folkloreTextCaches.get(source) || {};
}

/**
 * The English side of the Danish corpus: UCLA's published-text translations for
 * all 900 records, plus the field-diary renderings for 434. A separate file
 * (1,3 MB) fetched only when a reader actually asks for English, so the
 * Danish-reading majority never pays for it. Written by
 * scripts/build-danish-folklore-data.mjs.
 */
export interface DanishEnglishEntry { text?: string; manuscript?: string }
let danishEnglishCache: Record<string, DanishEnglishEntry> | null = null;
let danishEnglishLoading: Promise<void> | null = null;
export async function loadDanishEnglish(): Promise<Record<string, DanishEnglishEntry>> {
  if (danishEnglishCache) return danishEnglishCache;
  if (danishEnglishLoading) {
    await danishEnglishLoading;
    return danishEnglishCache ?? {};
  }
  danishEnglishLoading = (async () => {
    try {
      const res = await fetch("/data/danish-folklore-en.json");
      danishEnglishCache = res.ok ? await res.json() : {};
    } catch {
      danishEnglishCache = {};
    }
  })();
  await danishEnglishLoading;
  return danishEnglishCache ?? {};
}

/** Get text for a specific folklore record (returns undefined if not loaded yet) */
export function getFolkloreText(id: string): FolkloreTextEntry | undefined {
  // Try Sagnagrunnur texts
  const sgId = id.replace("sagnagrunnur-", "");
  const isTexts = folkloreTextCaches.get("icelandic");
  if (isTexts?.[sgId]) return isTexts[sgId];
  // Try Danish texts
  const dkTexts = folkloreTextCaches.get("danish");
  if (dkTexts?.[id]) return dkTexts[id];
  return undefined;
}

/**
 * Load Finnish folklore from pre-built SKVR (Suomen Kansan Vanhat Runot) dataset.
 * Finnish-Karelian folk poetry: Kalevala epics, incantations, lyric songs, legends.
 * One-time fetch from static JSON.
 */
let finnishFolkloreCache: FolkloreRecord[] | null = null;
export function getFinnishFolkloreCache(): FolkloreRecord[] | null { return finnishFolkloreCache; }
export async function loadFinnishFolklore(): Promise<FolkloreRecord[]> {
  if (finnishFolkloreCache) return finnishFolkloreCache;
  try {
    const res = await fetch("/data/finnish-folklore.json");
    if (!res.ok) return [];
    const data = await res.json();
    const finnishGenreMap: Record<string, string> = {
      loitsut: "sorcery",
      epiikka: "legends",
      legendat: "legends",
      lyriikka: "folklife",
    };
    finnishFolkloreCache = data.map((d: RawFolkloreRecord) => ({
      id: d.id,
      name: d.name || "Kansanruno",
      lng: d.lng,
      lat: d.lat,
      country: "FI" as const,
      source: "skvr" as const,
      title: d.name || "Kansanruno",
      text: d.text || null,
      year: d.year ? parseInt(String(d.year)) : undefined,
      category: (d.genre && finnishGenreMap[d.genre]) || "legends",
      placeName: d.placeName || "",
      landscape: d.region || undefined,
      collector: d.collector || undefined,
      genre: d.genre || undefined,
      sourceUrl: "https://skvr.fi/",
    }));
    return finnishFolkloreCache!;
  } catch (e) {
    warnFetch("loadFinnishFolklore failed:", e);
    return [];
  }
}

/**
 * Load Greenlandic folklore from curated static JSON.
 * ~28 Inuit/Greenlandic legends (Sedna, Tupilak, Qivittoq, etc.)
 * Based on public domain sources: Knud Rasmussen (1921), Hinrich Rink (1875).
 */
let greenlandicFolkloreCache: FolkloreRecord[] | null = null;
export function getGreenlandicFolkloreCache(): FolkloreRecord[] | null { return greenlandicFolkloreCache; }
export async function loadGreenlandicFolklore(): Promise<FolkloreRecord[]> {
  if (greenlandicFolkloreCache) return greenlandicFolkloreCache;
  try {
    const res = await fetch("/data/greenland-folklore.json");
    if (!res.ok) return [];
    const data = await res.json();
    greenlandicFolkloreCache = data.map((d: RawFolkloreRecord) => ({
      id: d.id,
      name: d.name || "Inuit-sagn",
      lng: d.lng,
      lat: d.lat,
      country: "GL" as Country,
      source: "greenland-curated" as const,
      title: d.name || "Inuit-sagn",
      text: d.text || null,
      nameEn: d.nameEn || undefined,
      summaryEn: d.summaryEn || undefined,
      category: d.category || "legends",
      placeName: d.placeName || "",
      sourceUrl: undefined,
    }));
    return greenlandicFolkloreCache!;
  } catch (e) {
    warnFetch("loadGreenlandicFolklore failed:", e);
    return [];
  }
}

/**
 * Load Estonia's register of sacred natural sites (looduslikud pühapaigad)
 * from a build-time snapshot of the Maa-amet WFS — 1628 hiis groves,
 * offering stones, sacred springs and cross-trees.
 *
 * These are ritual places rather than recorded stories, which is why they
 * land on the folklore layer as "folklife". Baked by
 * scripts/build-estonian-sacred-sites.mjs, which also does the EPSG:3301
 * reprojection the service refuses to do. Hiite Maja SA, CC BY 4.0.
 */
let estonianSacredCache: FolkloreRecord[] | null = null;
export function getEstonianSacredCache(): FolkloreRecord[] | null { return estonianSacredCache; }
export async function loadEstonianSacredSites(): Promise<FolkloreRecord[]> {
  if (estonianSacredCache) return estonianSacredCache;
  try {
    const res = await fetch("/data/estonian-sacred-sites.json");
    if (!res.ok) return [];
    const data = await res.json();
    estonianSacredCache = data.map((d: RawFolkloreRecord) => ({
      id: d.id,
      name: d.name || "Looduslik pühapaik",
      lng: d.lng,
      lat: d.lat,
      country: "EE" as Country,
      source: "estonia-sacred" as const,
      title: d.name || "Looduslik pühapaik",
      text: d.text || null,
      nameEn: d.nameEn || undefined,
      category: d.category || "folklife",
      placeName: d.placeName || "",
      sourceUrl: d.sourceUrl || undefined,
    }));
    return estonianSacredCache!;
  } catch (e) {
    warnFetch("loadEstonianSacredSites failed:", e);
    return [];
  }
}

/**
 * Load British & Isle of Man folklore from curated static JSON.
 * ~47 legends from England, Scotland, Wales, and Isle of Man.
 * Covers Arthurian legends, Celtic mythology, Mabinogion, fairy traditions, etc.
 * NOTE: Irish entries are in irish-mythology.json (richer metadata).
 */
let britishFolkloreCache: FolkloreRecord[] | null = null;
export function getBritishFolkloreCache(): FolkloreRecord[] | null { return britishFolkloreCache; }
export async function loadBritishFolklore(): Promise<FolkloreRecord[]> {
  if (britishFolkloreCache) return britishFolkloreCache;
  try {
    const res = await fetch("/data/british-folklore.json");
    if (!res.ok) return [];
    const data = await res.json();
    britishFolkloreCache = data
      .filter((d: RawFolkloreRecord) => d.country !== "IE") // Irish entries are in irish-mythology.json
      .map((d: RawFolkloreRecord) => ({
      id: d.id,
      name: d.name || "Legend",
      lng: d.lng,
      lat: d.lat,
      country: (d.country || "GB") as Country,
      source: "british-curated" as const,
      title: d.name || "Legend",
      text: d.text || null,
      nameEn: d.nameEn || undefined,
      textEn: d.textEn || undefined,
      category: d.category || "legends",
      placeName: d.placeName || "",
      sourceUrl: undefined,
    }));
    return britishFolkloreCache!;
  } catch (e) {
    warnFetch("loadBritishFolklore failed:", e);
    return [];
  }
}

/**
 * Load Irish mythology from curated static JSON.
 * ~80-100 major mythological sites across all four cycles + festival sites + fairy lore + saints.
 * Rich metadata: mythCycle, characters, festivalConnection, nameIrish, province.
 */
let irishMythologyCache: FolkloreRecord[] | null = null;
export function getIrishMythologyCache(): FolkloreRecord[] | null { return irishMythologyCache; }
export async function loadIrishMythology(): Promise<FolkloreRecord[]> {
  if (irishMythologyCache) return irishMythologyCache;
  try {
    const res = await fetch("/data/irish-mythology.json");
    if (!res.ok) return [];
    const data = await res.json();
    irishMythologyCache = data.map((d: RawFolkloreRecord) => ({
      id: d.id,
      name: d.name || "Irish Legend",
      lng: d.lng,
      lat: d.lat,
      country: (d.country || "IE") as Country,
      source: "irish-mythology" as const,
      title: d.name || "Irish Legend",
      text: d.text || null,
      nameEn: d.nameEn || undefined,
      textEn: d.textEn || undefined,
      category: d.category || "legends",
      placeName: d.placeName || "",
      sourceUrl: d.wikipediaUrl || undefined,
      mythCycle: d.mythCycle || undefined,
      characters: Array.isArray(d.characters) ? d.characters : undefined,
      festivalConnection: d.festivalConnection || undefined,
      festivalDate: d.festivalDate || undefined,
      nameIrish: d.nameIrish || undefined,
      province: d.province || undefined,
      wikidataId: d.wikidataId || undefined,
      wikipediaUrl: d.wikipediaUrl || undefined,
    }));
    return irishMythologyCache!;
  } catch (e) {
    warnFetch("loadIrishMythology failed:", e);
    return [];
  }
}

/**
 * Norse World — Uppsala University dataset of place-name references
 * attested in Old Swedish + Old Danish medieval texts. ~532 geo-located
 * "places mentioned in the medieval Nordic literary record". Pinned as
 * folklore records with source="norse-world" so they cluster with the
 * other intangible-heritage layers.
 *
 * Note: many entries point outside the Nordic region — the dataset
 * captures the WORLDVIEW of medieval Nordic writers, including
 * Constantinople, Jerusalem, mythical lands. We surface them all; the
 * value is the literary connection, not strict national bbox fit.
 */
let norseWorldCache: FolkloreRecord[] | null = null;
export function getNorseWorldCache(): FolkloreRecord[] | null { return norseWorldCache; }
export async function loadNorseWorld(): Promise<FolkloreRecord[]> {
  if (norseWorldCache) return norseWorldCache;
  try {
    const res = await fetch("/data/norse-world.json");
    if (!res.ok) return [];
    interface NWRecord { id: string; name: string; lat: number; lng: number; type?: string; description?: string; realFictional?: string; wiki?: string; geonames?: string }
    const data: NWRecord[] = await res.json();
    norseWorldCache = data.map((d) => ({
      id: d.id,
      name: d.name,
      lng: d.lng,
      lat: d.lat,
      country: determineCountry(d.lat, d.lng),
      source: "norse-world" as const,
      title: d.name,
      text: d.description || null,
      category: d.type || "norse-world",
      placeName: d.name,
      sourceUrl: d.wiki || d.geonames || undefined,
    }));
    return norseWorldCache;
  } catch (e) {
    warnFetch("loadNorseWorld failed:", e);
    return [];
  }
}
